import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import type { EntryComponent } from 'cms/entries/entries';
import { Desc } from 'components/landing-pages-v2/shared/Typography';
import type {
  ILpComponentContactButton,
  ILpComponentContactSection,
} from 'declarations/contentful';

import Grid from './Grid';

// CMS-authored hrefs are external data, so only allow known-safe schemes
// (blocks `javascript:`/`data:` while keeping real contact links such as
// https/tel/mailto/sms working). Parsed against a fixed base so relative hrefs
// resolve without depending on `window`, since this runs during SSR.
const ALLOWED_HREF_PROTOCOLS = new Set(['https:', 'http:', 'mailto:', 'tel:', 'sms:']);

const getSafeHref = (href: string): string | undefined => {
  const trimmed = href.trim();
  if (!trimmed) {
    return undefined;
  }
  try {
    const destination = new URL(trimmed, 'https://www.opendoor.com');
    return ALLOWED_HREF_PROTOCOLS.has(destination.protocol) ? trimmed : undefined;
  } catch {
    return undefined;
  }
};

const RenderContactButton = (entry: ILpComponentContactButton) => {
  const { fields } = entry;
  const analyticsName = `cosmos-landing-page-contact-button-${fields?.analyticsId}`;
  const href = getSafeHref(fields.href);
  if (!href) {
    return null;
  }
  return (
    <Button
      key={analyticsName}
      variant="primary"
      size="md"
      analyticsName={analyticsName}
      aria-label={fields.text}
      href={href}
      className="w-fit"
    >
      {fields.text}
    </Button>
  );
};

const RenderContactSection = (entry: ILpComponentContactSection) => {
  const { fields } = entry;
  const analyticsName = `cosmos-landing-page-contact-section-${fields?.analyticsId}`;

  return (
    <Grid>
      <Col
        analyticsName={analyticsName}
        trackImpression
        className="col-span-12 gap-8 py-20 lg:items-center"
      >
        <Col className="gap-5">
          <Col className="gap-2 lg:flex-row lg:justify-center lg:gap-3">
            <Text as="h2" className="text-header-sm lg:text-header-md">
              {fields?.title}
            </Text>
            <Text as="h2" className="text-header-sm lg:text-header-md">
              {fields?.subtitle}
            </Text>
          </Col>
          <Desc $largerThanMD={{ fontSize: 24 }}>{fields?.description}</Desc>
        </Col>
        <Row className="flex-wrap items-start gap-2 xl:gap-5">
          {entry.fields.contactButtons?.map((contactButton: ILpComponentContactButton) => {
            return RenderContactButton(contactButton);
          })}
        </Row>
      </Col>
    </Grid>
  );
};

const ContactSection: EntryComponent<ILpComponentContactSection> = {
  render: RenderContactSection,
};

export default ContactSection;
